import React, { FC, useState, useEffect } from "react";
import { Box, Typography, TextField, InputAdornment, Select, MenuItem, CircularProgress } from "@mui/material";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { CustomButton } from "../../../components";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getAllCashDrawerReasonsAction } from "../../../redux/actions/cashDrawerReasonsAction";

interface RemoveCashModalProps {
  open: boolean;
  onClose: () => void;
  onRemove: ({ amount, reasonId, optionalText }: { amount: number; reasonId: number; optionalText: string }) => void;
}

const RemoveCashModal: FC<RemoveCashModalProps> = ({
  open,
  onClose,
  onRemove,
}) => {
  const [cashAmount, setCashAmount] = useState<string>("");
  const [reasonId, setReasonId] = useState<number | "">("");
  const [optionalText, setOptionalText] = useState<string>("");

  const dispatch = useAppDispatch();
  const { cashDrawerReasons, cashDrawerReasonsLoading: reasonsLoading } = useAppSelector((state: any) => state.cashDrawerReason);

  useEffect(() => {
    if (open) {
      dispatch(getAllCashDrawerReasonsAction());
      setCashAmount("");
      setReasonId("");
      setOptionalText("");
    }
  }, [open, dispatch]);

  const handleCashAmountChange = (
    event: any
  ) => {
    const value = event.target.value.replace(/[^0-9.]/g, "");
    setCashAmount(value);
  };

  const handleRemoveClick = () => {
    const amount = parseFloat(cashAmount);
    if (!isNaN(amount) && typeof reasonId === "number") {
      onRemove({
        amount: amount,
        reasonId: reasonId,
        optionalText: optionalText.trim(),
      });
      onClose();
    }
  };

  return (
    <CustomModal
      title="Remove Cash"
      openModal={open}
      onClose={onClose}
      width="40.28571428571429rem"
      height="35rem"
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "2rem",
            alignItems: "center",
            marginTop: "2.5rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.428571428571429rem",
              color: Colors.MAIN_GRAY,
              width: "12rem",
            }}
          >
            Cash Amount
          </Typography>
          <TextField
            autoFocus={true}
            value={cashAmount}
            onChange={handleCashAmountChange}
            placeholder="0.00"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography
                    sx={{
                      fontSize: "1.428571428571429rem",
                      color: Colors.MAIN_GRAY,
                    }}
                  >
                    $
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              width: "22.21428571428571rem",
              "& .MuiOutlinedInput-root": {
                height: "2.785714285714286rem",
              },
              "& .MuiOutlinedInput-input": {
                textAlign: "left",
                fontSize: "1.428571428571429rem",
                color: Colors.MAIN_GRAY,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.MAIN_GRAY,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "2rem",
            alignItems: "center",
            marginTop: "1.5rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.428571428571429rem",
              color: Colors.MAIN_GRAY,
              width: "12rem",
            }}
          >
            Reason
          </Typography>
          <Select
            value={reasonId}
            onChange={(e) => setReasonId(e.target.value as number)}
            displayEmpty
            sx={{
              width: "22.21428571428571rem",
              height: "2.785714285714286rem",
              "& .MuiSelect-select": {
                textAlign: "left",
                fontSize: "1.428571428571429rem",
                color: Colors.MAIN_GRAY,
                paddingTop: "0.2rem",
                paddingBottom: "0.2rem",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.MAIN_GRAY,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select Reason
            </MenuItem>
            {reasonsLoading ? (
              <MenuItem value="" disabled>
                <CircularProgress size={20} /> Loading Reasons...
              </MenuItem>
            ) : (
              cashDrawerReasons &&
              cashDrawerReasons
                .filter((reason: any) => reason.type === "REMOVE")
                .map((reason: any) => (
                  <MenuItem key={reason.id} value={reason.id}>
                    {reason.reason}
                  </MenuItem>
                ))
            )}
          </Select>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "2rem",
            alignItems: "flex-start",
            marginTop: "1.5rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.428571428571429rem",
              color: Colors.MAIN_GRAY,
              marginTop: "1rem",
              width: "12rem",
            }}
          >
            Notes (Optional)
          </Typography>
          <TextField
            multiline
            value={optionalText}
            onChange={(e) => setOptionalText(e.target.value)}
            placeholder="Enter additional details..."
            sx={{
              width: "22.21428571428571rem",
              "& .MuiOutlinedInput-root": {
                minHeight: "5.285714285714286rem",
              },
              "& .MuiOutlinedInput-input": {
                textAlign: "left",
                fontSize: "1.428571428571429rem",
                color: Colors.MAIN_GRAY,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: Colors.MAIN_GRAY,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "2.3rem",
          }}
        >
          <CustomButton
            onClick={handleRemoveClick}
            disabled={!cashAmount.trim() || reasonId === ""}
            sx={{
              width: "33.35714285714286rem",
              height: "4.571428571428571rem",
              backgroundColor: Colors.PRIMARY,
              fontSize: "1.714285714285714rem",
              textTransform: "none",
            }}
          >
            Remove
          </CustomButton>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default RemoveCashModal;
